import React from "react";
import {
  UseFormRegister,
  Control,
  Controller,
  FieldErrors,
} from "react-hook-form";
import { AlertCircle, FileText } from "lucide-react";
import ProjectFormSection from "./ProjectFormSection";
import { ProjectCategory } from "../../../lib/types/adminProjects";

interface ProjectFormBasicInfoProps {
  register: UseFormRegister<any>;
  control: Control<any>;
  errors: FieldErrors<any>;
  isActive: boolean;
  onToggle: () => void;
  descriptionLength: number;
}

const CATEGORIES: ProjectCategory[] = [
  "AI Training",
  "Data Annotation",
  "Code Review",
  "Content Moderation",
  "Audio Transcription",
  "Sentiment Analysis",
  "Prompt Engineering",
  "Image Classification",
  "Text Review",
  "Video Annotation",
];

const getCategoryConfig = (category: string) => {
  const configs: Record<string, { bg: string; text: string }> = {
    "AI Training": { bg: "bg-blue-100", text: "text-blue-700" },
    "Data Annotation": { bg: "bg-emerald-100", text: "text-emerald-700" },
    "Code Review": { bg: "bg-purple-100", text: "text-purple-700" },
    "Content Moderation": { bg: "bg-amber-100", text: "text-amber-700" },
    "Audio Transcription": { bg: "bg-rose-100", text: "text-rose-700" },
    "Sentiment Analysis": { bg: "bg-cyan-100", text: "text-cyan-700" },
    "Prompt Engineering": { bg: "bg-indigo-100", text: "text-indigo-700" },
    "Image Classification": { bg: "bg-teal-100", text: "text-teal-700" },
    "Text Review": { bg: "bg-orange-100", text: "text-orange-700" },
    "Video Annotation": { bg: "bg-pink-100", text: "text-pink-700" },
  };
  return configs[category] || { bg: "bg-gray-100", text: "text-gray-700" };
};

const ProjectFormBasicInfo: React.FC<ProjectFormBasicInfoProps> = ({
  register,
  control,
  errors,
  isActive,
  onToggle,
  descriptionLength,
}) => {
  return (
    <ProjectFormSection
      id="basic"
      title="Basic Information"
      description="Project title, category, and description"
      icon={FileText}
      iconGradient="from-[#8B52FF] to-[#E05A6A]"
      isActive={isActive}
      onToggle={onToggle}
    >
      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
          Project Title <span className="text-red-500">*</span>
        </label>
        <input
          {...register("title")}
          type="text"
          placeholder="e.g., AI Response Evaluation"
          className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-lg sm:rounded-xl border-2 transition-all focus:outline-none ${
            errors.title
              ? "border-red-300 focus:border-red-500 bg-red-50"
              : "border-gray-200 focus:border-[#8B52FF] focus:ring-2 focus:ring-[#8B52FF]/20"
          }`}
        />
        {errors.title && (
          <p className="mt-1.5 text-xs sm:text-sm text-red-600 flex items-center gap-1">
            <AlertCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
            {errors.title.message as string}
          </p>
        )}
      </div>

      {/* Category */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
          Category <span className="text-red-500">*</span>
        </label>
        <Controller
          name="category"
          control={control}
          render={({ field }) => (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-1.5 sm:gap-2">
              {CATEGORIES.map((category) => {
                const config = getCategoryConfig(category);
                const isSelected = field.value === category;
                return (
                  <button
                    key={category}
                    type="button"
                    onClick={() => field.onChange(category)}
                    className={`p-2 sm:p-3 rounded-lg sm:rounded-xl border-2 text-xs sm:text-sm font-medium transition-all ${
                      isSelected
                        ? `${config.bg} ${config.text} border-current`
                        : "border-gray-200 text-gray-600 hover:border-gray-300"
                    }`}
                  >
                    {category}
                  </button>
                );
              })}
            </div>
          )}
        />
        {errors.category && (
          <p className="mt-1.5 text-xs sm:text-sm text-red-600 flex items-center gap-1">
            <AlertCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
            {errors.category.message as string}
          </p>
        )}
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
          Description <span className="text-red-500">*</span>
        </label>
        <textarea
          {...register("description")}
          rows={4}
          placeholder="Provide a detailed description..."
          className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-lg sm:rounded-xl border-2 transition-all focus:outline-none resize-none ${
            errors.description
              ? "border-red-300 focus:border-red-500 bg-red-50"
              : "border-gray-200 focus:border-[#8B52FF] focus:ring-2 focus:ring-[#8B52FF]/20"
          }`}
        />
        <div className="flex items-center justify-between mt-1.5 gap-2">
          {errors.description ? (
            <p className="text-xs sm:text-sm text-red-600 flex items-center gap-1 min-w-0">
              <AlertCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
              <span className="truncate">
                {errors.description.message as string}
              </span>
            </p>
          ) : (
            <span />
          )}
          <span className="text-xs text-gray-400 flex-shrink-0">
            {descriptionLength}/2000
          </span>
        </div>
      </div>
    </ProjectFormSection>
  );
};

export default ProjectFormBasicInfo;
export { getCategoryConfig, CATEGORIES };
